import React, { useMemo, useState, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import BaseModal from 'components/common/modal/BaseModal';
import { getData } from 'core/services/api';
import ImageWithBaseApi from 'components/common/mediaRender/ImageWithBaseApi';
import { FaEye, FaPhone, FaEnvelope, FaUserTag, FaCalendarAlt, FaBox, FaInfoCircle } from 'react-icons/fa';
import './ContactForm.css'; // Import your CSS file for styling
import moment from 'moment';
import { clientTypeOptionsList, typeOptionProduct } from 'core/constant/SELECT_OPTIONS';

const ContactForm = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchData = async (page = 1, perPage = 10) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getData(API_ENDPOINTS.CONTACT, { page, per_page: perPage });
            setData(response.data.data || []);
            setTotalRecords(response.data.total || 0);
        } catch (err) {
            setError(err);
            console.error('Error fetching contact data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(currentPage, rowsPerPage);
    }, [currentPage, rowsPerPage]);

    const handleOpenModal = (rowData) => {
        setSelectedRow(rowData);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedRow(null);
    };

    const handlePageChange = (event) => {
        setCurrentPage(event.page + 1);
        setRowsPerPage(event.rows);
    };

    const getClientTypeLabel = (value) => {
        const option = clientTypeOptionsList.find(opt => opt.value === value);
        return option ? option.label : 'No especificado';
    };

    const getOptionTypeLabel = (value) => {
        const option = typeOptionProduct.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    const handleDownloadExcel = async () => {
        try {
            const response = await getData(API_ENDPOINTS.CONTACT_EXPORT, {}, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'contacts.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Error downloading Excel file:', err);
        }
    };

    const columns = useMemo(() => [
        {
            field: 'created_at',
            header: 'Fecha',
            sortable: true,
            body: (rowData) => (
                <div className="d-flex align-items-center">
                    <FaCalendarAlt className="me-2 text-muted" />
                    {moment(rowData.created_at).format('DD/MM/YYYY HH:mm')}
                </div>
            ),
            style: { minWidth: '180px' }
        },
        { 
            field: 'name', 
            header: 'Contacto', 
            sortable: true,
            body: (rowData) => (
                <div>
                    <strong>{rowData.name}</strong>
                    <div className="d-flex align-items-center text-muted small">
                        <FaUserTag className="me-1" />
                        {getClientTypeLabel(rowData.user_type)}
                    </div>
                </div>
            )
        },
        { 
            field: 'email', 
            header: 'Correo/Teléfono', 
            sortable: true,
            body: (rowData) => (
                <div>
                    <div className="d-flex align-items-center">
                        <FaEnvelope className="me-2 text-primary" />
                        <a className="contact-form" href={`mailto:${rowData.email}`}>{rowData.email}</a>
                    </div>
                    {rowData.phone && (
                        <div className="d-flex align-items-center mt-1">
                            <FaPhone className="me-2 text-success" />
                            <a className="contact-form" href={`tel:${rowData.phone}`}>{rowData.phone}</a>
                        </div>
                    )}
                </div>
            ),
            style: { minWidth: '220px' }
        },
        {
            field: 'product.title',
            header: 'Producto',
            sortable: true,
            body: (rowData) => rowData.product ? (
                <div>
                    <FaBox className="me-2 text-info" />
                    {rowData.product.title}
                </div>
            ) : 'N/A'
        },
        {
            field: 'message',
            header: 'Mensaje',
            body: (rowData) => (
                <div className="text-truncate" style={{ maxWidth: '200px' }} title={rowData.message}>
                    {rowData.message || 'N/A'}
                </div>
            )
        },
        {
            field: 'actions',
            header: 'Acciones',
            body: (rowData) => (
                <button
                    className="btn btn-primary btn-sm d-flex align-items-center"
                    onClick={() => handleOpenModal(rowData)}
                >
                    <FaEye className="me-1" /> Ver
                </button>
            ),
            style: { width: '100px' }
        }
    ], []);

    const renderModalContent = (rowData) => {
        const hasProduct = !!rowData.product;
        const hasOptions = rowData.product_options && rowData.product_options.length > 0;
        
        return (
            <div className="contact-details-modal p-0">
                <div className="d-flex mb-4">
                    <div className="col-md-6">
                        <h5 className="section-title-form">
                            <FaUserTag className="me-2" />
                            Información del Contacto
                        </h5>
                        <div className="detail-item">
                            <label>Nombre:</label>
                            <p>{rowData.name}</p>
                        </div>
                        <div className="detail-item">
                            <label>Correo Electrónico:</label>
                            <p><a href={`mailto:${rowData.email}`}>{rowData.email}</a></p>
                        </div>
                        <div className="detail-item">
                            <label>Teléfono:</label>
                            <p><a href={`tel:${rowData.phone}`}>{rowData.phone || 'N/A'}</a></p>
                        </div>
                        <div className="detail-item">
                            <label>Tipo de Usuario:</label>
                            <p>{getClientTypeLabel(rowData.user_type)}</p>
                        </div>
                    </div>
                    
                    <div className="col-md-6">
                        <h5 className="section-title-form">
                            <FaCalendarAlt className="me-2" />
                            Fechas
                        </h5>
                        <div className="detail-item">
                            <label>Fecha de Registro:</label>
                            <p>{moment(rowData.created_at).format('DD/MM/YYYY HH:mm')}</p>
                        </div>
                        <div className="detail-item">
                            <label>Última Actualización:</label>
                            <p>{moment(rowData.updated_at).format('DD/MM/YYYY HH:mm')}</p>
                        </div>
                    </div>
                </div>

                <div className="d-flex mb-4">
                    <div className="col-12">
                        <h5 className="section-title-form">
                            <FaInfoCircle className="me-2" />
                            Mensaje
                        </h5>
                        <div className="message-content">
                            {rowData.message || 'No se proporcionó mensaje'}
                        </div>
                    </div>
                </div>

                {hasProduct && (
                    <div className="d-flex mb-4">
                        <div className="col-12">
                            <h5 className="section-title-form">
                                <FaBox className="me-2" />
                                Información del Producto
                            </h5>
                            <div className="detail-item">
                                <label>Producto:</label>
                                <p>{rowData.product.title}</p>
                            </div>
                            <div className="detail-item">
                                <label>Descripción:</label>
                                <p>{rowData.product.description || 'N/A'}</p>
                            </div>
                            {rowData.product.tags && (
                                <div className="detail-item">
                                    <label>Etiquetas:</label>
                                    <div className="tags-container">
                                        {rowData.product.tags.map((tag, index) => (
                                            <span key={index} className="tag-badge">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {hasOptions && (
                    <div className="d-flex">
                        <div className="col-12">
                            <h5 className="section-title-form">
                                Opciones del Producto
                            </h5>
                            <div className="options-grid option-list">
                                {rowData.product_options.map((option, index) => (
                                    <div key={index} className="option-card">
                                        <div className="option-header">
                                            <strong>{getOptionTypeLabel(option.type)}</strong>
                                        </div>
                                        <div className="option-body">
                                            {option.display_type === 'image' && option.url ? (
                                                <div className="option-image">
                                                    <ImageWithBaseApi
                                                        src={option.url}
                                                        alt={option.type}
                                                        className="img-thumbnail"
                                                    />
                                                    <div className="option-value">{option.value}</div>
                                                </div>
                                            ) : (
                                                <div className="option-text-value">
                                                    {option.value}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5>Error al cargar los datos</h5>
                <p>{error.message}</p>
                <button className="btn btn-secondary" onClick={() => fetchData(currentPage, rowsPerPage)}>
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="contact-form-container">
            <div className="page-header-form mb-4">
                <h2 className="page-title-form">
                    <FaEnvelope className="me-2" />
                    Formularios de Contacto
                </h2>
                <p className="page-subtitle">Listado de solicitudes y consultas de clientes</p>
                <hr className="header-divider" />
                <button className="btn btn-success" onClick={handleDownloadExcel}>
                    Descargar Excel
                </button>
            </div>

            <DataTableWrapper
                data={data}
                columns={columns}
                paginator={true}
                rowsPerPage={rowsPerPage}
                totalRecords={totalRecords}
                first={(currentPage - 1) * rowsPerPage}
                onPageChange={handlePageChange}
                sortField="created_at"
                emptyMessage="No se encontraron registros"
                sortOrder={-1}
                lazy={true}
                showGridlines={true}
                loading={loading}
                className="contact-data-table"
            />

            <BaseModal
                show={showModal}
                title={`Detalles del Contacto #${selectedRow?.id || ''}`}
                handleClose={handleCloseModal}
                size="lg"
            >
                {selectedRow && renderModalContent(selectedRow)}
            </BaseModal>
        </div>
    );
};

export default React.memo(ContactForm);